/**
 * The canonical scenario document — what `GET /api/scenarios/:id/export` hands
 * back and what the importer accepts.
 *
 * It is deliberately the *model* plus the few names a reader needs to know what
 * they are looking at, and nothing else. No KPIs: a summary in a file is derived
 * data that starts disagreeing with the model the moment either side of the
 * engine changes, and the importer would recompute it anyway. No ids that mean
 * anything on import either — `sourceId` is provenance, and the server mints
 * fresh ids for every row it stores (see lib/scenarioWrite.ts).
 *
 * Reading is generous and writing is strict, because the two jobs are different.
 * We emit exactly one shape; we accept three:
 *
 *  - this document,
 *  - a `znms-roi-scenario` v1 file from the page this app replaces, via
 *    {@link fromLegacyScenario},
 *  - a bare `ScenarioModel`, which is what a `ScenarioVersion.snapshot` is when
 *    somebody copies one out of an API response.
 *
 * Anything else is refused with the sentence the old page used, because a file
 * that imports as "all defaults" looks exactly like a silent wipe:
 * docs/08-frontend.md#import--export.
 */

import { EXPORT_SCHEMA, EXPORT_VERSION } from "../constants.js";
import { fromLegacyScenario, isLegacyScenarioDocument } from "./legacy.js";
import { looksLikeScenarioModel, normalizeScenarioModel } from "./normalize.js";
import type { ScenarioModel } from "./types.js";

export interface ScenarioDocument {
  schema: typeof EXPORT_SCHEMA;
  version: typeof EXPORT_VERSION;
  /** When the file was written. Informational; nothing reads it back. */
  exportedAt: string;
  product: { name: string; currencyCode: string };
  scenario: {
    name: string;
    description: string | null;
    /** Where it came from, for the person who finds the file in six months. */
    sourceId: string | null;
    sourceVersion: number | null;
  };
  model: ScenarioModel;
}

export interface BuildDocumentArgs {
  product: { name: string; currencyCode: string };
  scenario: {
    name: string;
    description?: string | null;
    id?: string | null;
    version?: number | null;
  };
  model: ScenarioModel;
  /** Injectable so a test does not have to freeze the clock. */
  now?: Date;
}

export function buildScenarioDocument(args: BuildDocumentArgs): ScenarioDocument {
  return {
    schema: EXPORT_SCHEMA,
    version: EXPORT_VERSION,
    exportedAt: (args.now ?? new Date()).toISOString(),
    product: { name: args.product.name, currencyCode: args.product.currencyCode },
    scenario: {
      name: args.scenario.name,
      description: args.scenario.description ?? null,
      sourceId: args.scenario.id ?? null,
      sourceVersion: args.scenario.version ?? null,
    },
    // Normalised on the way out as well as in. An export is also a restore
    // point, and a document written from a half-migrated row should land in the
    // same clamped, self-consistent state everything else does.
    model: normalizeScenarioModel(args.model, { currencyCode: args.model.currencyCode }),
  };
}

/** Which of the three accepted shapes a file turned out to be. */
export type DocumentFormat = "canonical" | "legacy" | "model";

export interface ParsedScenarioDocument {
  format: DocumentFormat;
  model: ScenarioModel;
  /** The name to give the scenario; the caller may let the user override it. */
  name: string;
  description: string | null;
  sourceId: string | null;
  sourceVersion: number | null;
  /** The product the file says it came from, when it says. */
  productName: string | null;
}

export type ScenarioDocumentParse =
  { ok: true; document: ParsedScenarioDocument } | { ok: false; reason: string };

/** The refusal, in the words the page this replaces used. */
export const UNRECOGNISED_DOCUMENT =
  "This isn't an ROI scenario — nothing changed. Choose a file exported from this app, or a znms-roi-scenario file from the old page.";

export interface ParseDocumentOptions {
  /**
   * The product's currency. It wins over whatever the file names: the product
   * owns the currency (docs/03), and importing a USD file into an MVR product
   * must not quietly price it in dollars.
   */
  currencyCode?: string;
  /** Used when the file names no scenario — a legacy file, or a bare model. */
  fallbackName?: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown, fallback: string): string;
function text(value: unknown, fallback: string | null): string | null;
function text(value: unknown, fallback: string | null): string | null {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed.length === 0 ? fallback : trimmed;
}

/**
 * Turns an arbitrary parsed JSON value into something importable, or says why
 * not. It never throws: the caller — a route or a file picker — decides what a
 * refusal looks like, and both of them want a sentence rather than an exception.
 */
export function parseScenarioDocument(
  raw: unknown,
  options: ParseDocumentOptions = {},
): ScenarioDocumentParse {
  const fallbackName = options.fallbackName ?? "Imported scenario";
  const source = asRecord(raw);
  if (!source) return { ok: false, reason: UNRECOGNISED_DOCUMENT };

  // `options.currencyCode` overrides rather than fills in: the product owns the
  // currency, and `normalizeScenarioModel` would otherwise keep the one the file
  // names. `fromLegacyScenario` already works this way.
  const normalize = (model: unknown): ScenarioModel => {
    const source = asRecord(model) ?? {};
    return normalizeScenarioModel(
      options.currencyCode === undefined
        ? source
        : { ...source, currencyCode: options.currencyCode },
      { currencyCode: options.currencyCode },
    );
  };

  // The stamp first: a canonical document that also happens to carry a legacy
  // key must not be read as legacy.
  if (source.schema === EXPORT_SCHEMA) {
    const scenario = asRecord(source.scenario) ?? {};
    const product = asRecord(source.product);
    const model = source.model;
    if (!looksLikeScenarioModel(model)) return { ok: false, reason: UNRECOGNISED_DOCUMENT };

    const version = scenario.sourceVersion;
    return {
      ok: true,
      document: {
        format: "canonical",
        model: normalize(model),
        name: text(scenario.name, fallbackName),
        description: text(scenario.description, null),
        sourceId: text(scenario.sourceId, null),
        sourceVersion: typeof version === "number" && Number.isInteger(version) ? version : null,
        productName: product ? text(product.name, null) : null,
      },
    };
  }

  if (isLegacyScenarioDocument(source)) {
    return {
      ok: true,
      document: {
        format: "legacy",
        model: fromLegacyScenario(source, { currencyCode: options.currencyCode }),
        name: text(source.name, fallbackName),
        description: null,
        sourceId: null,
        sourceVersion: null,
        productName: null,
      },
    };
  }

  // A bare model — a `ScenarioVersion.snapshot` copied out of an API response,
  // which is a thing people do and which is a perfectly good import.
  if (looksLikeScenarioModel(source)) {
    return {
      ok: true,
      document: {
        format: "model",
        model: normalize(source),
        name: text(source.name, fallbackName),
        description: null,
        sourceId: null,
        sourceVersion: null,
        productName: null,
      },
    };
  }

  return { ok: false, reason: UNRECOGNISED_DOCUMENT };
}

/**
 * A filename a person can find again: the product, the scenario and the day.
 * It ends in `.roi.json` so a folder of them is obvious at a glance and so the
 * importer's file picker has something to filter on.
 */
export function documentFilename(
  productName: string,
  scenarioName: string,
  now: Date = new Date(),
): string {
  const slug = (value: string) =>
    value
      .normalize("NFKD")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase()
      .slice(0, 40) || "scenario";
  return `${slug(productName)}-${slug(scenarioName)}-${now.toISOString().slice(0, 10)}.roi.json`;
}
